// storeImport.ts
import { getSafeAxiosClient } from "@/helpers/axiosProxy";
import { defineStore } from "pinia";
import { useNotificationsStore } from "@/store/notificationsStore";
import { ref, computed } from 'vue'

/** Representerer en valgbar import-aktivitet */
export interface ActivityInfo {
  key: string;
  label: string;
  description: string;
}

interface ImportActivities {
  baseActivities: Record<string, string>;
  selectableActivities: ActivityInfo[];
}

export const useImportStore = defineStore("importStore", () => {
  const notificationsStore = useNotificationsStore();
  const importActivities = ref<ImportActivities | null>(null);
  const isLoadingActivities = ref(false);
  const isTruncatingTable = ref(false);
  const selectedSteps = ref<Record<string, boolean>>({});

  /** Hjelpe-computed for å hente selectable activity keys */
  const selectableActivityKeys = computed(() =>
    importActivities.value?.selectableActivities.map(a => a.key) ?? []
  );

  async function fetchImportActivities () {
    // Unngå duplikat-kall hvis allerede lastet
    if (importActivities.value) return;

    isLoadingActivities.value = true;
    try {
      const axiosClient = await getSafeAxiosClient();
      const response = await axiosClient.get<ImportActivities>('v2/import/activities');
      importActivities.value = response.data;

      // Initialiser base activities som true (siden de er obligatoriske)
      if (response.data.baseActivities) {
        Object.keys(response.data.baseActivities).forEach(key => {
          selectedSteps.value[key] = true;
        });
      }

      // Initialiser selectable activities som false
      if (response.data.selectableActivities) {
        response.data.selectableActivities.forEach(activity => {
          selectedSteps.value[activity.key] = false;
        });
      }
    } catch (error) {
      console.error('Feil ved henting av import aktiviteter:', error);
      notificationsStore.addError('Kunne ikke hente import aktiviteter');
      throw error;
    } finally {
      isLoadingActivities.value = false;
    }
  }

  async function startImport (uploadId: string, importDefinitionId: string, importType: string, fileName: string, debugOn: boolean = false, selectedActivities?: string[], extraPayload?: any) {
    try {
      const axiosClient = await getSafeAxiosClient();
      const params = { uploadId };

      // NY: Bruk valgte aktiviteter hvis sendt med, ellers bruk selectedSteps fra store
      let stepsToUse: Record<string, boolean>;

      if (selectedActivities && selectedActivities.length > 0) {
        // Konverter selectedActivities array til Record<string, boolean>
        stepsToUse = {};
        // Alle base activities er alltid true
        if (importActivities.value?.baseActivities) {
          Object.keys(importActivities.value.baseActivities).forEach(key => {
            stepsToUse[key] = true;
          });
        }
        // Kun valgte selectable activities er true
        if (importActivities.value?.selectableActivities) {
          importActivities.value.selectableActivities.forEach(activity => {
            stepsToUse[activity.key] = selectedActivities.includes(activity.key);
          });
        }
      } else {
        // Fallback til eksisterende logikk
        stepsToUse = selectedSteps.value;
      }

      // Bygg body med kun felter som backend forventer i ImportJobRequest
      const body = {
        importDefinitionId: Number(importDefinitionId),
        importType,
        debugOn,
        emailReceipt: extraPayload?.emailReceipt ?? true
      };

      const response = await axiosClient.post('v2/import', body, { params });

      if (response) {
        const jobType = importType === 'ImportSimulation' ? 'simulering' : 'import';
        notificationsStore.addSuccess(`Startet ${jobType} (${fileName})`);
      }
      return response?.data;
    } catch (error) {
      console.error('Feil ved start av import:', error);
      notificationsStore.addError('Kunne ikke starte import');
      throw error;
    }
  }

  async function truncateTable (tableDefinitionId: string) {
    isTruncatingTable.value = true;
    try {
      const axiosClient = await getSafeAxiosClient();
      const response = await axiosClient.post(`v2/import/truncate-table`, null, { params: { tableDefinitionId } });

      if (response.status >= 200 && response.status < 300) {
        notificationsStore.addSuccess('Tabell ble tømt');
        return true;
      } else {
        notificationsStore.addError('Kunne ikke tømme tabell');
        return false;
      }
    } catch (error) {
      console.error('Feil ved tømming av tabell:', error);
      notificationsStore.addError('Kunne ikke tømme tabell');
      return false;
    } finally {
      isTruncatingTable.value = false;
    }
  }

  function updateStep (key: string, value: boolean) {
    selectedSteps.value[key] = value;
  }
  function getActiveSteps () {
    return Object.entries(selectedSteps.value)
      .filter(([_, value]) => value)
      .map(([key]) => key);
  }

  return {
    importActivities,
    isLoadingActivities,
    isTruncatingTable,
    selectedSteps,
    selectableActivityKeys,
    fetchImportActivities,
    startImport,
    truncateTable,
    updateStep,
    getActiveSteps,
  };
});
